// src/features/spotting/pages/SightingDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, auth } from '../../../firebase';
import { IMGBB_API_KEY } from '../../../config';
import { 
  collection, query as firestoreQuery, where, orderBy, onSnapshot, doc, getDoc, 
  addDoc, updateDoc, deleteDoc, setDoc, serverTimestamp, increment, getDocs, limit 
} from 'firebase/firestore';
import { 
  Clock, MapPin, Bell, BellOff, ArrowLeft, ChevronRight, ChevronLeft,
  Loader2, X, ArrowBigUp, ArrowBigDown, Flame, Star, 
  Youtube, Video, Play, ExternalLink, Camera, MessageSquare
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import axios from 'axios';
import { getRelativeTime, getFullDate } from '../../../utils/helpers';
import { UPDATE_TYPES, LOGO_URL } from '../../../utils/constants';
import { applyWatermark } from '../../../utils/imageUtils';
import { ManualLocationModal } from '../../../components/modals/ManualLocationModal';

// --- FLAIR CONFIGURATION ---
const FLAIRS = {
  'chamstwo': {
    label: '💢 CHAMSTWO',
    color: 'bg-red-600',
    textColor: 'text-white'
  },
  'pinned': {
    label: '📌 PINNED',
    color: 'bg-yellow-500',
    textColor: 'text-white'
  },
  'alert-zone': {
    label: '🚨 ALERT ZONE',
    color: 'bg-red-500',
    textColor: 'text-white'
  },
  'exclusive-garage': {
    label: '🏆 EXCLUSIVE GARAGE',
    color: 'bg-purple-600',
    textColor: 'text-white'
  },
  'poradnik': {
    label: '📰 PORADNIK',
    color: 'bg-blue-600',
    textColor: 'text-white'
  }
};

// --- KOMPONENT GRAFICZNEJ TABLICY ---
const VisualPlate = ({ number }) => {
  if (!number) return null;
  const cleanNumber = number.replace(/\s/g, '').substring(0, 9);
  // No formatting - display as continuous string
  const formatted = cleanNumber;

  return (
    <div className="relative inline-flex items-center justify-center select-none shadow-sm rounded-sm overflow-hidden" 
         style={{ 
           width: '160px', height: '35px', 
           backgroundImage: "url('/plate_template.png')",
           backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat'
         }}>
      <span className="font-bold text-gray-900 tracking-tighter uppercase" 
            style={{ fontFamily: 'monospace', fontSize: '1.15rem', marginLeft: '20px', letterSpacing: '-0.5px' }}>
        {formatted}
      </span>
    </div>
  );
};

// --- POMOCNIK WIDEO ---
const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:.*v=|.*\/|.*embed\/|.*shorts\/))([^#&?"'>]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  if (url.includes('facebook.com')) return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0`;
  return null;
};

const VideoPlayer = ({ url }) => {
  const embedUrl = getEmbedUrl(url);
  if (!url) return null;
  return (
    <div className="mt-4 rounded-2xl overflow-hidden bg-black aspect-video relative shadow-lg border border-gray-800">
      {embedUrl ? (
        <iframe width="100%" height="100%" src={embedUrl} frameBorder="0" allowFullScreen title="Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center h-full text-white p-4 text-center">
          <Play size={32} className="text-red-500 mb-2" />
          <span className="text-xs font-bold underline">Obejrzyj wideo zewnętrzne</span>
        </a>
      )}
    </div>
  );
};

export const SightingDetail = ({ showToast, setZoomImg, isPoradnik = false }) => {
  const { identifier } = useParams(); // ZMIENIONE: z 'id' na 'identifier'
  const [searchParams] = useSearchParams();
  const highlightAddPhoto = searchParams.get('highlight') === 'addPhoto';
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [updateType, setUpdateType] = useState('COMMENT');
  const [comments, setComments] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [commentImage, setCommentImage] = useState(null);
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [usingGPS, setUsingGPS] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // --- DUAL ROUTING: ID lub plateSlug ---
  useEffect(() => {
    const fetchSighting = async () => {
      setLoading(true);
      let sightingData = null;
      let sightingId = null;

      try {
        // 1. Sprawdź czy to Firebase ID (długi hash, >15 znaków)
        if (identifier.length > 15 && !identifier.includes('-')) {
          console.log('Fetching by Firebase ID:', identifier);
          const docRef = doc(db, "sightings", identifier);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            sightingData = { id: docSnap.id, ...docSnap.data() };
            sightingId = docSnap.id;
            
            // REDIRECT do SEO URL (jeśli ma plateSlug)
            if (sightingData.plateSlug && !isPoradnik) {
              console.log('Redirecting to plateSlug:', sightingData.plateSlug);
              navigate(`/pojazd/${sightingData.plateSlug}`, { replace: true });
              return;
            }
          }
        } 
        // 2. Poradnik (SEO slug)
        else if (isPoradnik) {
          console.log('Fetching poradnik by seoSlug:', identifier);
          const q = firestoreQuery(
            collection(db, 'sightings'),
            where('seoSlug', '==', identifier.toLowerCase()),
            limit(1)
          );
          
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            sightingData = { id: docSnap.id, ...docSnap.data() };
            sightingId = docSnap.id;
          }
        }
        // 3. Query by plateSlug (nowy SEO URL)
        else {
          console.log('Fetching by plateSlug:', identifier);
          const q = firestoreQuery(
            collection(db, 'sightings'),
            where('plateSlug', '==', identifier.toLowerCase()),
            limit(1)
          );
          
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            sightingData = { id: docSnap.id, ...docSnap.data() };
            sightingId = docSnap.id;
          }
        }

        if (sightingData && sightingId) {
          setCar(sightingData);
          
          // Setup real-time listener for comments
          const commentsQuery = firestoreQuery(
            collection(db, "sightings", sightingId, "comments"),
            orderBy("createdAt", "asc")
          );
          
          const unsubComments = onSnapshot(commentsQuery, (snap) => {
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          });

          // Check if following
          if (auth.currentUser) {
            const followDoc = await getDoc(doc(db, "follows", `${auth.currentUser.uid}_${sightingId}`));
            setIsFollowing(followDoc.exists());
          }

          setLoading(false);
          
          return () => {
            unsubComments();
          };
        } else {
          console.log('Sighting not found');
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching sighting:', error);
        navigate('/');
      }
    };

    fetchSighting();
  }, [identifier, navigate, isPoradnik]);

  // Auto-scroll to timeline when redirected from duplicate check
  useEffect(() => {
    if (window.location.hash === '#timeline') {
      setTimeout(() => {
        const timeline = document.getElementById('timeline-section');
        if (timeline) {
          timeline.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  const toggleFollow = async () => {
    if (!auth.currentUser) return showToast("Zaloguj się", "error");
    if (!car) return;
    
    const followId = `${auth.currentUser.uid}_${car.id}`;
    if (isFollowing) {
      await deleteDoc(doc(db, "follows", followId));
      setIsFollowing(false);
    } else {
      await setDoc(doc(db, "follows", followId), { 
        userId: auth.currentUser.uid, 
        sightingId: car.id, 
        createdAt: serverTimestamp() 
      });
      setIsFollowing(true);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const watermarkedBlob = await applyWatermark(file);
      const formData = new FormData();
      formData.append('image', watermarkedBlob);
      const res = await axios.post(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, formData);
      setCommentImage(res.data.data.url);
      setUpdateType('PHOTO');
      showToast("Zdjęcie gotowe");
    } catch (err) {
      console.error(err);
      showToast("Błąd przesyłania", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLocationManual = (name, lat, lng) => {
    setComment(name);
    setUpdateType('LOCATION');
    postComment(null, { name, lat, lng });
  };

  const handleTypeClick = (type) => {
    if (type === 'photo') fileInputRef.current?.click();
    if (type === 'location') {
      setUpdateType('LOCATION');
      if (navigator.geolocation) {
        setUsingGPS(true);
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            setUsingGPS(false);
            try {
              const { latitude, longitude } = pos.coords;
              const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=pl`);
              const data = await res.json();
              const name = data.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
              setComment(name);
              postComment(null, { name, lat: latitude, lng: longitude });
            } catch {
              showToast("Błąd geolokalizacji", "error");
              setUsingGPS(false);
            }
          },
          () => {
            setUsingGPS(false);
            setShowManualLocation(true);
          }
        );
      } else {
        setShowManualLocation(true);
      }
    }
  };

  const postComment = async (e, locationOverride) => {
    if (e) e.preventDefault();
    if (!auth.currentUser) return showToast("Zaloguj się", "error");
    if (!car) return;
    if (!comment.trim() && !commentImage && !videoUrl && !locationOverride) return showToast("Dodaj treść", "error");

    try {
      const payload = {
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        type: updateType,
        upvotes: 0,
        downvotes: 0
      };

      if (comment.trim()) payload.text = comment;
      if (commentImage) payload.imageUrl = commentImage;
      if (videoUrl) payload.videoUrl = videoUrl;

      if (locationOverride) {
        payload.locationName = locationOverride.name;
        payload.lat = locationOverride.lat;
        payload.lng = locationOverride.lng;
      }

      await addDoc(collection(db, "sightings", car.id, "comments"), payload);
      await updateDoc(doc(db, "sightings", car.id), { commentCount: increment(1) });

      setComment("");
      setCommentImage(null);
      setVideoUrl("");
      setShowVideoInput(false);
      setUpdateType('COMMENT');
      showToast("Dodano");
    } catch (err) {
      console.error(err);
      showToast("Błąd", "error");
    }
  };

  const voteComment = async (commentId, delta) => {
    if (!auth.currentUser) return showToast("Zaloguj się", "error");
    if (!car) return;
    
    try {
      const field = delta > 0 ? "upvotes" : "downvotes";
      await updateDoc(doc(db, "sightings", car.id, "comments", commentId), {
        [field]: increment(1)
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getUpdateLabel = (type) => {
    const updateType = UPDATE_TYPES[type];
    // Wyciągnij label z obiektu
    return updateType?.label || type;
  };

  if (loading || !car) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const images = car.images || [];
  const mapLocations = [
    car.location?.lat && car.location?.lng && { lat: car.location.lat, lng: car.location.lng, label: "Miejsce zgłoszenia" },
    ...comments.filter(c => c.lat && c.lng).map((c, i) => ({ lat: c.lat, lng: c.lng, label: `Update #${i + 1}` }))
  ].filter(Boolean);

  // SEO canonical URL
  const canonicalUrl = isPoradnik 
    ? `https://motoradar24.pl/poradnik/${car.seoSlug || identifier}`
    : `https://motoradar24.pl/pojazd/${car.plateSlug || identifier}`;

  // Flair config
  const flairConfig = FLAIRS[car.flair] || FLAIRS['chamstwo'];

  return (
    <>
      <Helmet>
        {/* CANONICAL TAG - Google indexuje tylko ten URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* SEO dla poradników */}
        {isPoradnik && car.seoTitle ? (
          <>
            <title>{car.seoTitle}</title>
            <meta name="description" content={car.seoDescription || car.description} />
            <meta name="keywords" content={car.seoKeywords?.join(', ') || ''} />
          </>
        ) : (
          <>
            <title>{car.plate ? `${car.plate} - ${car.brand || 'Pojazd'}` : car.brand || 'Pojazd'} | Motoradar24</title>
            <meta name="description" content={`${car.plate || ''} ${car.brand || ''} - ${car.description?.substring(0, 150) || 'Szczegóły pojazdu'}`} />
            <meta name="keywords" content={`${car.plate || ''}, ${car.brand || ''}, chamstwo drogowe, złe parkowanie, ${car.locationName || ''}`} />
          </>
        )}
        
        {/* Open Graph */}
        <meta property="og:title" content={car.seoTitle || `${car.plate} - ${car.brand}`} />
        <meta property="og:description" content={car.seoDescription || car.description} />
        <meta property="og:image" content={images[0] || '/og-image.jpg'} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content={isPoradnik ? 'article' : 'website'} />
      </Helmet>

      {showManualLocation && <ManualLocationModal onClose={() => setShowManualLocation(false)} onConfirm={handleLocationManual} />}

      <div className="max-w-2xl mx-auto p-4 pb-20">
        <div className="mb-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 text-sm hover:text-blue-600 transition-colors">
            <ArrowLeft size={18}/> Powrót
          </button>
        </div>

        {/* --- KARTA SAMOCHODU --- */}
        <div className="bg-white rounded-2xl border overflow-hidden mb-4 shadow-sm">
          {/* FLAIR BADGE */}
          {car.flair && (
            <div className={`${flairConfig.color} ${flairConfig.textColor} px-4 py-2 text-xs font-black tracking-wider text-center`}>
              {flairConfig.label}
            </div>
          )}

          {/* GALERIA ZDJĘĆ */}
          {images.length > 0 && (
            <div className="relative group">
              <img src={images[currentImageIndex]} className="w-full h-80 object-cover cursor-zoom-in" onClick={() => setZoomImg(images[currentImageIndex])} alt="car" />
              {images.length > 1 && (
                <>
                  <button onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-600 hover:text-white">
                    <ChevronLeft size={20}/>
                  </button>
                  <button onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-blue-600 hover:text-white">
                    <ChevronRight size={20}/>
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
          )}

          {/* INFO */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-black text-gray-900 mb-2">{car.brand}</h1>
                {car.plate && <VisualPlate number={car.plate} />}
              </div>
              <button onClick={toggleFollow} className={`p-3 rounded-xl transition-all ${isFollowing ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {isFollowing ? <BellOff size={20}/> : <Bell size={20}/>}
              </button>
            </div>
            
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
              <Clock size={14}/> {getFullDate(car.createdAt)}
              <span>•</span>
              <MapPin size={14}/> {car.locationName}
            </div>
            
            {/* DESCRIPTION - HTML dla poradników, plain text dla chamstwa */}
            {car.flair === 'poradnik' ? (
              <div 
                className="text-sm text-gray-700 mb-4 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: car.description }}
              />
            ) : (
              <p className="text-sm text-gray-700 mb-4">{car.description}</p>
            )}
            
            <VideoPlayer url={car.videoUrl} />
          </div>

          {/* GŁÓWNA MAPA */}
          {mapLocations.length > 0 && (
            <div className="p-4 h-64">
              <MapContainer 
                center={[mapLocations[0].lat, mapLocations[0].lng]} 
                zoom={10} 
                className="h-full w-full rounded-xl border shadow-inner"
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                {mapLocations.map((l, i) => (
                    <Marker key={i} position={[l.lat, l.lng]}><Popup>{l.label}</Popup></Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </div>

        {/* --- FORMULARZ AKTUALIZACJI --- */}
        <div className="bg-white rounded-2xl border p-4 mb-4">
          <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-gray-800"><Clock size={18} className="text-blue-600"/> Dodaj aktualizację</h3>
          
          <form onSubmit={postComment} className="space-y-3">
             <div className="flex gap-2 relative">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageSelect} />
                
                <div className="group relative">
                    <button type="button" onClick={() => handleTypeClick('location')} className={`p-3 rounded-xl transition-all ${updateType === 'LOCATION' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {usingGPS ? <Loader2 size={20} className="animate-spin"/> : <MapPin size={20}/>}
                    </button>
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Dodaj pozycję GPS</span>
                </div>

                <div className="group relative">
                    <button 
                      type="button" 
                      onClick={() => handleTypeClick('photo')} 
                      className={`
                        p-3 rounded-xl transition-all 
                        ${updateType === 'PHOTO' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                        ${highlightAddPhoto ? 'animate-pulse ring-4 ring-blue-400' : ''}
                      `}
                    >
                        {uploadingImage ? <Loader2 size={20} className="animate-spin"/> : <Camera size={20}/>}
                    </button>
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Dodaj zdjęcie</span>
                </div>

                <div className="group relative">
                    <button type="button" onClick={() => setShowVideoInput(!showVideoInput)} className={`p-3 rounded-xl transition-all ${videoUrl ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        <Youtube size={20}/>
                    </button>
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Dodaj film URL</span>
                </div>

                <div className="group relative">
                    <button type="button" onClick={() => setUpdateType('COMMENT')} className={`p-3 rounded-xl transition-all ${updateType === 'COMMENT' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        <MessageSquare size={20}/>
                    </button>
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Dodaj komentarz</span>
                </div>
             </div>

             {showVideoInput && (
                <input type="url" placeholder="Link YouTube/Facebook..." className="w-full p-3 bg-red-50 border border-red-100 rounded-xl text-xs outline-none focus:border-red-400" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} />
             )}

             {commentImage && (
                <div className="relative inline-block mt-2">
                    <img src={commentImage} className="h-24 w-24 object-cover rounded-xl border-2 border-blue-500" alt="preview"/>
                    <button type="button" onClick={() => setCommentImage(null)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-lg"><X size={12}/></button>
                </div>
             )}

             <textarea placeholder="Napisz coś o tym pojazdzie..." className="w-full bg-gray-50 p-4 rounded-xl text-sm outline-none border-2 border-transparent focus:border-blue-500 transition-all" rows="2" value={comment} onChange={e => setComment(e.target.value)} />
             <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-xl font-black text-xs tracking-widest uppercase shadow-lg active:scale-95 transition-all">
                Opublikuj aktualizację
             </button>
          </form>
        </div>

        {/* --- OŚ CZASU (HISTORIA) --- */}
        <div id="timeline-section" className="bg-white rounded-2xl border p-4">
          <h3 className="font-bold mb-6 flex items-center gap-2 text-sm text-gray-800"><Clock size={18} className="text-blue-600"/> Historia aktywności</h3>
          
          <div className="space-y-6 relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-100"></div>
            {comments.map((c) => (
              <div key={c.id} className="relative pl-10">
                <div className={`absolute left-2.5 top-2 w-3.5 h-3.5 rounded-full border-2 border-white z-10 shadow-sm ${c.type === 'LOCATION' ? 'bg-blue-600' : c.type === 'PHOTO' ? 'bg-green-500' : c.type === 'VIDEO' ? 'bg-red-500' : 'bg-gray-400'}`}></div>
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-[11px] font-black text-gray-800 uppercase tracking-tighter">
                        Spotter-{c.userId.substring(0,4)} <span className="text-blue-600 ml-1">{getUpdateLabel(c.type)}</span>
                    </p>
                    <span className="text-[10px] text-gray-400 font-bold">{getRelativeTime(c.createdAt)}</span>
                  </div>

                  {c.imageUrl && <img src={c.imageUrl} className="w-full rounded-xl mb-3 cursor-zoom-in border" onClick={() => setZoomImg(c.imageUrl)} alt="upd" />}
                  <VideoPlayer url={c.videoUrl} />
                  
                  {c.locationName && (
                    <div className="mb-3">
                        <p className="text-[11px] font-bold text-blue-600 mb-2 flex items-center gap-1">
                            <MapPin size={12}/> {c.locationName}
                        </p>
                        {c.lat && c.lng && (
                            <div className="h-32 rounded-xl overflow-hidden border border-gray-100">
                                <MapContainer 
                                    center={[c.lat, c.lng]} 
                                    zoom={13} 
                                    style={{ height: '100%', width: '100%' }}
                                    scrollWheelZoom={false}
                                    dragging={false}
                                    zoomControl={false}
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                                    <Marker position={[c.lat, c.lng]}></Marker>
                                </MapContainer>
                            </div>
                        )}
                    </div>
                  )}

                  {c.text && <p className="text-sm text-gray-600 leading-relaxed italic">"{c.text}"</p>}

                  <div className="flex gap-4 mt-4 pt-3 border-t border-gray-50">
                    <button type="button" onClick={() => voteComment(c.id, 1)} className="flex items-center gap-1.5 text-gray-400 hover:text-green-600 transition-colors">
                        <ArrowBigUp size={20}/> <span className="text-xs font-black">{(c.upvotes || 0) - (c.downvotes || 0)}</span>
                    </button>
                    <button type="button" onClick={() => voteComment(c.id, -1)} className="text-gray-400 hover:text-red-600 transition-colors">
                        <ArrowBigDown size={20}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};